import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { currentCountry } from "@/jurisdictions/country";
import { FR_TEAM_UI_ENABLED } from "@/jurisdictions/features";
import Layout from "@/components/Layout";
import AppShell from "@/components/AppShell";
import CookieConsent from "@/components/CookieConsent";
import Login from "@/pages/Login";
import ForcePassword from "@/pages/ForcePassword";
const ActivateAccount = lazy(() => import("@/pages/ActivateAccount"));
const FranceAdmin = lazy(() => import("@/pages/FranceAdmin"));
const FranceAgents = lazy(() => import("@/pages/FranceAgents"));
const FranceWorkspace = lazy(() => import("@/jurisdictions/FranceWorkspace"));
const Brand = lazy(() => import("@/pages/Brand"));
const Veille = lazy(() => import("@/pages/Veille"));
const Legal = lazy(() => import("@/pages/Legal"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Agent = lazy(() => import("@/pages/Agent"));
const CabinetSettings = lazy(() => import("@/pages/CabinetSettings"));
const PaieHistory = lazy(() => import("@/pages/PaieHistory"));
const CoffreRh = lazy(() => import("@/pages/CoffreRh"));
const ReferentielCgi = lazy(() => import("@/pages/ReferentielCgi"));
const ReferentielTravail = lazy(() => import("@/pages/ReferentielTravail"));
const ReferentielSocietes = lazy(() => import("@/pages/ReferentielSocietes").then((module) => ({ default: module.ReferentielSocietes })));
const RechercheJuridique = lazy(() => import("@/pages/RechercheJuridique"));
const ReferentielDgi = lazy(() => import("@/pages/ReferentielDgi"));
const CalendrierEcheances = lazy(() => import("@/pages/CalendrierEcheances"));
const Clients = lazy(() => import("@/pages/Clients"));
const Missions = lazy(() => import("@/pages/Missions"));
const ReferentielGrandLivres = lazy(() => import("@/pages/ReferentielGrandLivres"));
const MarquerFait = lazy(() => import("@/pages/MarquerFait"));
const FrenchPortal = lazy(() => import("@/jurisdictions/FrenchPortal"));
const EspaceClient = lazy(() => import("@/pages/EspaceClient"));
const Widget = lazy(() => import("@/pages/Widget"));
const VerifyAttestation = lazy(() => import("@/pages/VerifyAttestation"));
const AdminPlatform = lazy(() => import("@/pages/AdminPlatform"));
const Bootstrap = lazy(() => import("@/pages/Bootstrap"));
const SuperadminBootstrap = lazy(() => import("@/pages/SuperadminBootstrap"));
const Connectors = lazy(() => import("@/pages/Connectors"));

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePassword />;
  if (user.role === "client" && location.pathname.startsWith("/app")) return <Navigate to="/espace-client" replace />;
  return children;
}

function PublicHome() {
  const { user, ready } = useAuth();
  if (ready && user) return <Navigate to={user.role === "client" ? "/espace-client" : "/app"} replace />;
  return <Login />;
}

function TeamEntry() {
  const { search } = useLocation();
  const client = new URLSearchParams(search).get("client");
  if (FR_TEAM_UI_ENABLED) return <FranceAgents />;
  return <Navigate replace to={client && /^[a-f0-9]{24}$/.test(client) ? `/app?client=${client}` : "/app"} />;
}

function LoginRoute() {
  const { user, ready } = useAuth();
  if (ready && user) return <Navigate to={user.role === "client" ? "/espace-client" : "/app"} replace />;
  return <Login />;
}

function AppRoutes() {
  const { ready, instanceError } = useAuth();
  if (instanceError) return <main className="p-10 max-w-xl mx-auto space-y-4" role="alert"><h1>Connexion à l’instance indisponible</h1><p>{instanceError}</p><button onClick={() => window.location.reload()} className="underline">Réessayer</button></main>;
  if (!ready) return <div className="p-10" role="status">Chargement de votre instance…</div>;
  const france = currentCountry() === "FR";
  const coverage = <FranceWorkspace view="coverage" />;
  const sources = <FranceWorkspace view="sources" />;
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/activation" element={<ActivateAccount />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/inscription" element={<Navigate to="/login" replace />} />
      <Route path="/brand" element={<Brand />} />
      <Route path="/mentions-legales" element={<Legal kind="mentions" />} />
      <Route path="/confidentialite" element={<Legal kind="privacy" />} />
      <Route path="/conditions-service" element={<Legal kind="terms" />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/widget" element={<Widget />} />
      <Route path="/verifier" element={<VerifyAttestation />} />
      <Route path="/bootstrap-8f3a2c9d" element={france ? <Navigate to="/login" replace /> : <Bootstrap />} />
      <Route path="/superadmin" element={<SuperadminBootstrap />} />
      <Route path="/marquer-fait" element={<MarquerFait />} />
      <Route path="/espace-client" element={<Protected>{france ? <FrenchPortal /> : <EspaceClient />}</Protected>} />
      <Route
        path="/app"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<Agent />} />
        <Route element={<AppShell />}>
          <Route path="parametres" element={<CabinetSettings />} />
          <Route path="paie-historique" element={france ? coverage : <PaieHistory />} />
          <Route path="coffre-rh" element={france ? coverage : <CoffreRh />} />
          <Route path="referentiel-cgi" element={france ? sources : <ReferentielCgi />} />
          <Route path="referentiel-travail" element={france ? sources : <ReferentielTravail />} />
          <Route path="referentiel-sa" element={france ? sources : <ReferentielSocietes loi="sa" />} />
          <Route path="referentiel-sarl" element={france ? sources : <ReferentielSocietes loi="sarl" />} />
          <Route path="recherche-juridique" element={france ? sources : <RechercheJuridique />} />
          <Route path="referentiel-dgi" element={france ? sources : <ReferentielDgi />} />
          <Route path="veille" element={france ? coverage : <Veille />} />
          <Route path="calendrier" element={<CalendrierEcheances />} />
          <Route path="clients" element={france ? <FranceWorkspace key="clients" /> : <Clients />} />
          <Route path="fonctions" element={france ? coverage : <Navigate to="/app" replace />} />
          <Route path="agents" element={france ? <TeamEntry /> : <Navigate to="/app" replace />} />
          <Route path="missions" element={<Missions />} />
          <Route path="connecteurs" element={<Connectors />} />
          <Route path="grands-livres" element={france ? <FranceWorkspace key="fec" initialTab="fec" /> : <ReferentielGrandLivres />} />
          <Route path="admin" element={france ? <FranceAdmin /> : <AdminPlatform />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="p-8 text-sm text-muted-foreground">Chargement de la page…</div>}><AppRoutes /></Suspense>
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
